import React, { useState, useEffect } from 'react';
import Grid from "@mui/material/Grid";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaWhatsapp, FaEnvelope, FaMobile, FaLock, FaCheck } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useMetaPixel } from '../../contexts/MetaPixelContext';
import { useTranslation } from 'react-i18next';
import GoogleOneTap from '../../components/GoogleOneTap';
import { useDebounce } from 'use-debounce';
import Logo from '../../images/logo.svg';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './style.css';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

// WhatsApp business number for verification (update with your number)
const WHATSAPP_BUSINESS_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '21627256408'; // Configurable via env

const SignUpPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, error, clearError } = useAuth();
    const { getUtmParams, getAdClickIds, trackSignup, trackError, trackFormSubmitted, trackSignupStarted, trackEvent } = useAnalytics();
    const { trackCompleteRegistration } = useMetaPixel();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // Step: 1=Info, 2=Verify Choice, 3=Code, 4=Password
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [verificationMethod, setVerificationMethod] = useState(null);
    const [pendingUserId, setPendingUserId] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [showGuestModal, setShowGuestModal] = useState(false);

    // Cooldown timer effect
    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setInterval(() => {
                setResendCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        whatsapp: '',
        email: '',
        verification_code: '',
        password: '',
        confirm_password: '',
    });

    const [errors, setErrors] = useState({});

    // WhatsApp Verification State
    const [whatsappStatus, setWhatsappStatus] = useState(null); // 'checking', 'valid', 'invalid', 'unregistered'
    const [whatsappProfilePic, setWhatsappProfilePic] = useState(null);

    // Password strength tracking
    const [passwordStrength, setPasswordStrength] = useState({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false
    });

    // Detect mobile device
    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
        setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    }, []);

    // Restore signup session from sessionStorage on mount
    useEffect(() => {
        const restoreSession = async () => {
            const savedSession = sessionStorage.getItem('signup_session');

            if (savedSession) {
                try {
                    const session = JSON.parse(savedSession);
                    console.log('[Signup] Restoring session:', session);

                    // Restore form data
                    if (session.formData) {
                        setFormData(prev => ({ ...prev, ...session.formData }));
                    }

                    // Restore verification state
                    if (session.pendingUserId) {
                        setPendingUserId(session.pendingUserId);
                        setVerificationMethod(session.verificationMethod || null);

                        // Check current verification status from backend
                        try {
                            const response = await fetch(`${API_BASE}/api/v1/auth/check-verification-status/${session.pendingUserId}`);
                            const data = await response.json();

                            if (data.found && data.verified) {
                                // Already verified - go to password step
                                console.log('[Signup] Session verified, going to step 4');

                                // Restore form data from backend if not in local session
                                if (data.form_data && (!session.formData || !session.formData.first_name)) {
                                    console.log('[Signup] Restoring formData from backend:', data.form_data);
                                    setFormData(prev => ({
                                        ...prev,
                                        first_name: data.form_data.first_name || prev.first_name,
                                        last_name: data.form_data.last_name || prev.last_name,
                                        email: data.form_data.email || prev.email,
                                        whatsapp: data.form_data.whatsapp || prev.whatsapp
                                    }));
                                }

                                setStep(4);
                                toast.success(t('signupPage.welcomeBack'));
                            } else if (data.found) {
                                // Session exists but not verified - resume at last step
                                setStep(Math.min(session.step || 1, 3));
                            } else {
                                // Session expired on backend - start fresh
                                console.log('[Signup] Session expired on backend, starting fresh');
                                sessionStorage.removeItem('signup_session');
                            }
                        } catch (err) {
                            console.error('[Signup] Error checking verification status:', err);
                            // Fallback to saved step
                            setStep(session.step || 1);
                        }
                    }
                } catch (err) {
                    console.error('[Signup] Error restoring session:', err);
                    sessionStorage.removeItem('signup_session');
                }
            }

            // Check for guest account from level test
            const guestId = localStorage.getItem('guest_id');
            const fromLevelTest = location.state?.fromGuestLevelTest;

            if (guestId) {
                if (fromLevelTest) {
                    setShowGuestModal(true);
                } else if (!savedSession) {
                    // Only show toast if not restoring session
                    toast.info(t('signupPage.saveResults'), {
                        autoClose: false,
                        icon: '📝'
                    });
                }
            }

            // Track signup started (only if not resuming)
            if (!savedSession) {
                trackSignupStarted('direct');
            }
        };

        restoreSession();
    }, [location.state, trackSignupStarted]);

    // Save signup session to sessionStorage when key state changes
    useEffect(() => {
        // Only save if we have a pending registration
        if (pendingUserId) {
            const session = {
                pendingUserId,
                step,
                verificationMethod,
                formData: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    whatsapp: formData.whatsapp
                },
                savedAt: new Date().toISOString()
            };
            sessionStorage.setItem('signup_session', JSON.stringify(session));
            console.log('[Signup] Session saved:', session);
        }
    }, [pendingUserId, step, verificationMethod, formData.first_name, formData.last_name, formData.email, formData.whatsapp]);

    // Step timing tracking
    const startTime = React.useRef(Date.now());
    const prevStep = React.useRef(step);

    useEffect(() => {
        const now = Date.now();
        if (prevStep.current !== step) {
            const duration = (now - startTime.current) / 1000;
            trackEvent('signup_step_complete', {
                step: prevStep.current,
                duration_seconds: duration,
                next_step: step
            });
            startTime.current = now;
            prevStep.current = step;
        }
    }, [step, trackEvent]);

    // Poll for email verification status when on step 3 with email method
    useEffect(() => {
        if (step !== 3 || verificationMethod !== 'email' || !pendingUserId) {
            return;
        }

        let pollInterval;
        let attempts = 0;
        const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5s)

        const checkVerification = async () => {
            try {
                const response = await fetch(`${API_BASE}/api/v1/auth/check-verification-status/${pendingUserId}`);
                const data = await response.json();

                if (data.verified) {
                    clearInterval(pollInterval);
                    toast.success(t('signupPage.accountVerified'));
                    setStep(4);
                    return;
                }

                attempts++;
                if (attempts >= maxAttempts) {
                    clearInterval(pollInterval);
                    toast.warning('Verification timed out. Please try again.');
                }
            } catch (error) {
                console.error('Error checking verification status:', error);
            }
        };

        // Poll every 5 seconds
        pollInterval = setInterval(checkVerification, 5000);
        // Also check immediately
        checkVerification();

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [step, verificationMethod, pendingUserId]);

    const action = location.state?.action;

    const [debouncedEmail] = useDebounce(formData.email, 500);
    const [debouncedPhone] = useDebounce(formData.whatsapp, 500);
    const [debouncedFirstName] = useDebounce(formData.first_name, 500);
    const [debouncedLastName] = useDebounce(formData.last_name, 500);

    // Instant identification when email is typed
    useEffect(() => {
        if (debouncedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedEmail)) {
            // Track email entry but DO NOT identify yet to avoid creating ghost profiles
            trackEvent('signup_email_entered', { email: debouncedEmail });
        }
    }, [debouncedEmail, trackEvent]);

    // Instant identification when phone is typed (basic length check)
    useEffect(() => {
        if (debouncedPhone && debouncedPhone.length > 8) {
            // Track phone entry but DO NOT identify yet
            trackEvent('signup_phone_entered', { phone: debouncedPhone });
        }
    }, [debouncedPhone, trackEvent]);

    // Track name entry (debounced)
    useEffect(() => {
        if (debouncedFirstName) {
            trackEvent('signup_name_entered', { first_name: debouncedFirstName });
        }
    }, [debouncedFirstName, trackEvent]);

    useEffect(() => {
        if (debouncedLastName) {
            trackEvent('signup_surname_entered', { last_name: debouncedLastName });
        }
    }, [debouncedLastName, trackEvent]);

    // Check WhatsApp Registration & Get Profile Pic
    useEffect(() => {
        const checkWhatsApp = async () => {
            // Reset if input is cleared or too short
            if (!debouncedPhone || debouncedPhone.length < 8) {
                setWhatsappStatus(null);
                setWhatsappProfilePic(null);
                return;
            }

            setWhatsappStatus('checking');
            try {
                // Consolidated validation call (faster & cleaner)
                const res = await fetch(`${API_BASE}/api/v1/contacts/validate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ number: debouncedPhone })
                });
                const data = await res.json();

                if (data.registered) {
                    setWhatsappStatus('valid');
                    setWhatsappProfilePic(data.profile_pic || null);
                } else {
                    setWhatsappStatus('unregistered');
                    setWhatsappProfilePic(null);
                }
            } catch (err) {
                console.error('WhatsApp check failed', err);
                setWhatsappStatus('error'); // Silent fail
            }
        };
        checkWhatsApp();
    }, [debouncedPhone]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        if (error) clearError();

        // Update password strength in real-time
        if (name === 'password') {
            setPasswordStrength({
                minLength: value.length >= 8,
                hasUppercase: /[A-Z]/.test(value),
                hasLowercase: /[a-z]/.test(value),
                hasNumber: /\d/.test(value)
            });
        }
    };

    // Field tracking on blur
    const handleBlur = (field) => {
        trackEvent('signup_field_blurred', {
            field,
            value_length: formData[field]?.length || 0,
            step,
            has_error: !!errors[field]
        });
    };

    // Step 1 validation
    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) newErrors.first_name = t('signupPage.firstNameRequired');
        if (!formData.last_name.trim()) newErrors.last_name = t('signupPage.lastNameRequired');
        if (!formData.whatsapp.trim() && !formData.email.trim()) {
            newErrors.whatsapp = t('signupPage.contactRequired');
            newErrors.email = t('signupPage.contactRequired');
        }
        if (!isMobile && !formData.email.trim()) {
            newErrors.email = t('signupPage.emailRequiredDesktop');
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('signupPage.invalidEmail');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Step 1 submission
    const handleStep1Submit = async (e) => {
        e.preventDefault();
        if (!validateStep1()) {
            toast.error(t('signupPage.fillAllFields'));
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE}/api/v1/auth/register-pending`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    first_name: formData.first_name.trim(),
                    last_name: formData.last_name.trim(),
                    whatsapp: formData.whatsapp.replace(/\D/g, ''),
                    email: formData.email.trim() || null,
                    attribution: {
                        ...getUtmParams(),
                        ...getAdClickIds()
                    }
                })
            });

            const data = await response.json();
            if (response.ok && data.pending_id) {
                setPendingUserId(data.pending_id);
                setStep(2);
            } else {
                throw new Error(data.error?.message || t('signupPage.registrationFailed'));
            }
        } catch (err) {
            toast.error(err.message || t('signupPage.somethingWrong'));
            trackError('registration_step1_failed', err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // WhatsApp verification link
    const getWhatsAppVerificationLink = () => {
        const message = encodeURIComponent(
            `VERIFY:${pendingUserId}\n\nHi! I'd like to verify my Anas Arabic account.\n\nName: ${formData.first_name} ${formData.last_name}`
        );
        return `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${message}`;
    };

    // Poll for WhatsApp verification message
    const pollForWhatsAppMessage = async () => {
        let attempts = 0;
        const maxAttempts = 30; // Poll for up to 60 seconds

        const poll = async () => {
            if (attempts >= maxAttempts) {
                toast.warning(t('signupPage.timeout'));
                return;
            }

            try {
                const response = await fetch(`${API_BASE}/api/v1/auth/check-whatsapp-verification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pending_id: pendingUserId })
                });

                const data = await response.json();

                if (data.found) {
                    toast.success(t('signupPage.whatsappVerified'));
                    setStep(3);
                    return;
                }

                attempts++;
                setTimeout(poll, 2000); // Poll every 2 seconds
            } catch (err) {
                console.error('Polling error:', err);
                attempts++;
                setTimeout(poll, 2000);
            }
        };

        poll();
    };

    // WhatsApp verification
    const handleWhatsAppVerification = () => {
        setVerificationMethod('whatsapp');
        const whatsappLink = getWhatsAppVerificationLink();

        if (isMobile) {
            window.location.href = whatsappLink;
        } else {
            window.open(whatsappLink, '_blank');
        }

        // Start polling for the verification message
        toast.info(t('signupPage.waitingWhatsapp'));
        setTimeout(() => {
            pollForWhatsAppMessage();
            setStep(3); // Show code input immediately
        }, 1500);
        setResendCooldown(60);
    };

    // Email verification
    const handleEmailVerification = async () => {
        setVerificationMethod('email');
        setIsSubmitting(true);

        try {
            const response = await fetch(`${API_BASE}/api/v1/auth/send-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pending_id: pendingUserId, method: 'email' })
            });

            const data = await response.json();
            if (response.ok) {
                toast.success(t('signupPage.verificationSentEmail'));
                setStep(3);
            } else {
                throw new Error(data.error?.message || t('signupPage.sendFailed'));
            }
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
            setResendCooldown(60);
        }
    };

    // Code verification
    const handleCodeVerification = async (e) => {
        e.preventDefault();
        if (!formData.verification_code || formData.verification_code.length !== 6) {
            setErrors({ verification_code: t('signupPage.enterCode') });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE}/api/v1/auth/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pending_id: pendingUserId,
                    code: formData.verification_code,
                    method: verificationMethod
                })
            });

            const data = await response.json();
            if (response.ok && data.verified) {
                toast.success(t('signupPage.accountVerified'));
                setStep(4);
            } else {
                throw new Error(data.error?.message || t('signupPage.invalidCode'));
            }
        } catch (err) {
            toast.error(err.message);
            setErrors({ verification_code: err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Password validation - matches backend requirements
    const validatePassword = () => {
        const newErrors = {};
        const pwd = formData.password;

        if (!pwd) {
            newErrors.password = t('signupPage.passwordRequired');
        } else {
            const issues = [];
            if (pwd.length < 8) issues.push(t('signupPage.reqLength'));
            if (!/[A-Z]/.test(pwd)) issues.push(t('signupPage.reqUpper'));
            if (!/[a-z]/.test(pwd)) issues.push(t('signupPage.reqLower'));
            if (!/\d/.test(pwd)) issues.push(t('signupPage.reqNumber'));

            if (issues.length > 0) {
                newErrors.password = t('signupPage.passwordRequirements', { requirements: issues.join(', ') });
            }
        }

        if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = t('signupPage.passwordMismatch');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    // Final registration
    const handlePasswordSetup = async (e) => {
        e.preventDefault();
        if (!validatePassword()) return;

        setIsSubmitting(true);
        try {
            const guestId = localStorage.getItem('guest_id');
            let response;
            let endpoint = '/api/v1/auth/complete-registration';
            let body = {
                pending_id: pendingUserId,
                password: formData.password
            };

            // Check if guest_id is a valid UUID (has dashes and is 36 chars)
            // Old guest IDs might be integers - these should be cleared
            const isValidUUID = guestId && guestId.length === 36 && guestId.includes('-');

            // If guest account exists with valid UUID, call upgrade endpoint instead
            if (guestId && isValidUUID) {
                console.log('[Signup] Using upgrade-guest with valid UUID:', guestId);
                endpoint = '/api/v1/auth/upgrade-guest';
                body = {
                    guest_id: guestId,  // UUID string
                    email: formData.email,
                    password: formData.password,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    whatsapp: formData.whatsapp,
                    whatsapp_verified: verificationMethod === 'whatsapp'
                };
            } else if (guestId && !isValidUUID) {
                // Clear stale non-UUID guest_id
                console.log('[Signup] Clearing stale guest_id:', guestId);
                localStorage.removeItem('guest_id');
            }

            response = await fetch(`${API_BASE}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (response.ok && data.user) {
                // Clear guest ID after successful upgrade
                if (guestId) {
                    localStorage.removeItem('guest_id');
                }

                // Clear signup session - registration complete
                sessionStorage.removeItem('signup_session');
                console.log('[Signup] Session cleared after successful registration');

                // Generate unified event ID for cross-system correlation
                const eventId = trackCompleteRegistration({ email: data.user.email || formData.email });

                // Track in OpenPanel with same event_id for correlation
                trackSignup(verificationMethod, data.user.id, data.user.email || formData.email,
                    formData.first_name, formData.last_name, formData.whatsapp, verificationMethod === 'whatsapp');
                trackFormSubmitted('signup_form', true);
                trackEvent('meta_event_correlation', { event_id: eventId, event_name: 'CompleteRegistration' });
                toast.success(t('signupPage.accountCreated'));

                if (data.access_token) {
                    localStorage.setItem('access_token', data.access_token);
                    localStorage.setItem('refresh_token', data.refresh_token);
                }
                navigate('/dashboard');
            } else {
                throw new Error(data.error?.message || t('signupPage.registrationFailed'));
            }
        } catch (err) {
            toast.error(err.message);
            trackError('registration_complete_failed', err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get step title
    const getStepTitle = () => {
        switch (step) {
            case 1: return { title: t('signup.step1.title'), subtitle: t('signup.step1.subtitle') };
            case 2: return { title: t('signup.step2.title'), subtitle: t('signup.step2.subtitle') };
            case 3: return {
                title: t('signup.step3.title'),
                subtitle: verificationMethod === 'whatsapp'
                    ? t('signup.step3.subtitleWhatsapp')
                    : t('signup.step3.subtitleEmail')
            };
            case 4: return { title: t('signup.step4.title'), subtitle: t('signup.step4.subtitle') };
            default: return { title: '', subtitle: '' };
        }
    };

    const stepInfo = getStepTitle();

    return (
        <Grid className="loginWrapper" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="auth-page-header">
                <Link to="/" className="back-to-home">
                    <FaArrowLeft />
                </Link>
                <Link to="/" className="auth-logo">
                    <img src={Logo} alt="Anas Logo" />
                </Link>
                <div style={{ width: 40 }}></div>
            </div>

            {/* Guest Results Modal */}
            <Dialog
                open={showGuestModal}
                onClose={() => setShowGuestModal(false)}
                aria-labelledby="guest-modal-title"
                aria-describedby="guest-modal-description"
                PaperProps={{
                    style: {
                        borderRadius: 20,
                        padding: '10px',
                        maxWidth: '500px'
                    },
                }}
            >
                <div style={{ textAlign: 'center', padding: '20px 10px 10px' }}>
                    <div style={{
                        fontSize: '50px',
                        marginBottom: '10px',
                        background: '#f0f7ff',
                        width: '90px',
                        height: '90px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        🎉
                    </div>
                </div>
                <DialogTitle id="guest-modal-title" sx={{ textAlign: 'center', pt: 0, fontWeight: 800, fontSize: '24px', color: '#062265' }}>
                    {t('signupPage.assessmentCompleted')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="guest-modal-description" sx={{ textAlign: 'center', fontSize: '16px', color: '#555', mb: 2 }}>
                        <span dangerouslySetInnerHTML={{ __html: t('signupPage.assessmentDesc') }}></span>
                    </DialogContentText>

                    <div style={{
                        background: '#fff9e6',
                        padding: '15px',
                        borderRadius: '12px',
                        border: '1px solid #ffb100',
                        textAlign: 'center',
                        fontSize: '14px',
                        color: '#8a6d0b',
                        marginTop: '15px'
                    }}>
                        <span dangerouslySetInnerHTML={{ __html: t('signupPage.bonus') }}></span>
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                    <Button
                        onClick={() => setShowGuestModal(false)}
                        variant="contained"
                        className="cBtnTheme"
                        fullWidth
                        sx={{ py: 1.5, fontSize: '16px', fontWeight: 700 }}
                    >
                        {t('signupPage.createAccount')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid className="loginForm">
                <div className="shape-img">
                    <span className="fi flaticon-quran"></span>
                </div>

                {/* Step Progress */}
                <div className="signup-progress">
                    {[1, 2, 3, 4].map((s) => (
                        <React.Fragment key={s}>
                            <div className={`progress-step ${step >= s ? 'active' : ''} ${step > s ? 'completed' : ''}`}>
                                {step > s ? <FaCheck /> : s}
                            </div>
                            {s < 4 && <div className={`progress-line ${step > s ? 'active' : ''}`}></div>}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step Header */}
                <h2>{stepInfo.title}</h2>
                <p>{stepInfo.subtitle}</p>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Step 1: Basic Info */}
                {step === 1 && (


                    <form onSubmit={handleStep1Submit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    className="inputOutline"
                                    fullWidth
                                    name="first_name"
                                    label={t('signup.step1.firstName')}
                                    placeholder="Ahmed"
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('first_name')}
                                    error={!!errors.first_name}
                                    helperText={errors.first_name}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    autoComplete="given-name"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    className="inputOutline"
                                    fullWidth
                                    name="last_name"
                                    label={t('signup.step1.lastName')}
                                    placeholder="Hassan"
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('last_name')}
                                    error={!!errors.last_name}
                                    helperText={errors.last_name}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    autoComplete="family-name"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div className="phone-input-wrapper">
                                    <label className="phone-input-label">{t('signup.step1.whatsapp')}</label>
                                    <div className="phone-input-inner">
                                        <PhoneInput
                                            country={'fr'}
                                            value={formData.whatsapp}
                                            onChange={(value) => {
                                                setFormData(prev => ({ ...prev, whatsapp: value }));
                                                if (errors.whatsapp) setErrors(prev => ({ ...prev, whatsapp: '' }));
                                            }}
                                            onBlur={() => handleBlur('whatsapp')}
                                            disabled={isSubmitting}
                                            inputProps={{
                                                name: 'whatsapp',
                                                required: false,
                                                autoComplete: 'tel'
                                            }}
                                            containerClass={`phone-container ${errors.whatsapp ? 'error' : ''}`}
                                            inputClass="phone-input"
                                            buttonClass="phone-button"
                                            dropdownClass="phone-dropdown"
                                            searchClass="phone-search"
                                            enableSearch={true}
                                            searchPlaceholder="Search country..."
                                            preferredCountries={['fr', 'us', 'gb', 'de', 'tn', 'ma', 'dz', 'ae', 'sa']}
                                        />
                                        <FaWhatsapp className="whatsapp-field-icon" />
                                    </div>
                                    {errors.whatsapp && <span className="phone-error">{errors.whatsapp}</span>}

                                    {/* Whatsapp Status & Profile Pic Display */}
                                    {whatsappStatus && (
                                        <div className={`whatsapp-status-indicator ${whatsappStatus}`}>
                                            {whatsappStatus === 'checking' && (
                                                <span className="status-text">{t('signup.step1.whatsappChecking')}</span>
                                            )}
                                            {whatsappStatus === 'valid' && (
                                                <div className="whatsapp-found">
                                                    {whatsappProfilePic ? (
                                                        <img src={whatsappProfilePic} alt="Profile" className="wa-profile-pic" />
                                                    ) : (
                                                        <div className="wa-icon-valid">
                                                            <FaWhatsapp />
                                                        </div>
                                                    )}
                                                    <span className="status-text success">{t('signup.step1.whatsappFound')}</span>
                                                </div>
                                            )}
                                            {whatsappStatus === 'unregistered' && (
                                                <span className="status-text warning">{t('signup.step1.whatsappNotFound')}</span>
                                            )}
                                        </div>
                                    )}

                                    <span className="phone-hint">{t('signup.step1.whatsappHint')}</span>
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    className="inputOutline"
                                    fullWidth
                                    name="email"
                                    label={t('signup.step1.email')}
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('email')}
                                    error={!!errors.email}
                                    helperText={errors.email || t('signup.step1.emailOptional')}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    type="email"
                                    autoComplete="email"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: <FaEnvelope className="field-icon" />,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    className="cBtnTheme"
                                    disabled={isSubmitting}
                                    sx={{ py: 1.5, fontSize: '16px', fontWeight: 600, mt: 1 }}
                                >
                                    {isSubmitting ? (
                                        <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> Creating Account...</>
                                    ) : (
                                        `${t('signup.step1.continue')} →`
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                        <p className="noteHelp">{t('signup.step1.haveAccount')} <Link to="/login">{t('signup.step1.signIn')}</Link></p>
                    </form>
                )}

                {/* Step 2: Verification Choice */}
                {step === 2 && (
                    <div className="verification-options">

                        {/* Mobile: WhatsApp (Primary) */}
                        {isMobile && formData.whatsapp && (
                            <button
                                className="verify-option whatsapp"
                                onClick={handleWhatsAppVerification}
                                disabled={isSubmitting}
                            >
                                <div className="option-icon-wrapper whatsapp">
                                    <FaWhatsapp />
                                </div>
                                <div className="option-info">
                                    <h4>{t('signup.step2.whatsappOption')}</h4>
                                    <span>{t('signup.step2.whatsappDesc')}</span>
                                    {isMobile && (
                                        <span className="mobile-hint">
                                            <FaMobile /> {t('signup.step2.whatsappMobile')}
                                        </span>
                                    )}
                                </div>
                                <span className="option-arrow">→</span>
                            </button>
                        )}

                        {/* Mobile Separator */}
                        {isMobile && formData.whatsapp && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                margin: '20px 0',
                                color: '#666'
                            }}>
                                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                                <span style={{ padding: '0 10px', fontSize: '14px' }}>OR</span>
                                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                            </div>
                        )}

                        {/* Google Auth - Desktop (Primary) & Mobile (Secondary) */}
                        <div style={{ marginBottom: '20px', width: '100%' }}>
                            <GoogleOneTap
                                autoPrompt={!isMobile}
                                showButton={true}
                                buttonText={isMobile ? t('signupPage.continueGoogle') : t('signupPage.signupGoogle')}
                                context="signup"
                            />
                        </div>
                        {/* Email OTP - For users without WhatsApp or Google */}
                        {formData.email && (
                            <>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    margin: '20px 0',
                                    color: '#666'
                                }}>
                                    <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                                    <span style={{ padding: '0 10px', fontSize: '14px' }}>OR</span>
                                    <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                                </div>
                                <button
                                    className="verify-option email"
                                    onClick={handleEmailVerification}
                                    disabled={isSubmitting}
                                >
                                    <div className="option-icon-wrapper email">
                                        <FaEnvelope />
                                    </div>
                                    <div className="option-info">
                                        <h4>{t('signup.step2.emailOption')}</h4>
                                        <span>{t('signup.step2.emailDesc')}</span>
                                    </div>
                                    {isSubmitting ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <span className="option-arrow">→</span>
                                    )}
                                </button>
                            </>
                        )}
                        <button className="back-btn" onClick={() => setStep(1)}>
                            ← {t('signup.step2.back')}
                        </button>
                    </div>
                )}

                {/* Step 3: Code Verification */}
                {step === 3 && (
                    <>
                        {/* Email Verification - Show "Check Your Email" UI */}
                        {verificationMethod === 'email' ? (
                            <div className="email-verification-ui">
                                <div className="code-icon-wrapper email-sent">
                                    <FaEnvelope />
                                </div>

                                <h3 style={{ color: '#062265', marginBottom: '10px', fontSize: '20px' }}>
                                    {t('signupPage.checkEmail')}
                                </h3>

                                <p style={{ color: '#666', marginBottom: '25px', fontSize: '15px', lineHeight: 1.6 }}>
                                    <span dangerouslySetInnerHTML={{ __html: t('signupPage.sentLinkTo', { email: formData.email }) }}></span>
                                    {t('signupPage.clickLink')}
                                </p>

                                <div style={{
                                    background: '#f0f7ff',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    marginBottom: '25px',
                                    border: '1px solid #cce0ff'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <CircularProgress size={20} sx={{ color: '#004585' }} />
                                        <span style={{ color: '#004585', fontWeight: 600 }}>{t('signupPage.waitingVerification')}</span>
                                    </div>
                                    <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>
                                        {t('signupPage.autoContinue')}
                                    </p>
                                </div>

                                <div style={{
                                    background: '#fff9e6',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    border: '1px solid #ffb100'
                                }}>
                                    <p style={{ color: '#8a6d0b', fontSize: '13px', margin: 0 }}>
                                        <span dangerouslySetInnerHTML={{ __html: t('signupPage.spamTip') }}></span>
                                    </p>
                                </div>

                                <div className="resend-area">
                                    <span>{t('signup.step3.didntReceive')}</span>
                                    <button
                                        type="button"
                                        onClick={handleEmailVerification}
                                        className="resend-link"
                                        disabled={resendCooldown > 0 || isSubmitting}
                                        style={{ opacity: resendCooldown > 0 ? 0.5 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' }}
                                    >
                                        {resendCooldown > 0 ? `${t('signup.step3.resendIn')} ${resendCooldown}s` : t('signup.step3.resend')}
                                    </button>
                                </div>

                                <button type="button" className="back-btn" onClick={() => setStep(2)}>
                                    ← {t('signup.step3.tryDifferent')}
                                </button>
                            </div>
                        ) : (
                            /* WhatsApp Verification - Show OTP Input */
                            <form onSubmit={handleCodeVerification}>
                                <div className="code-icon-wrapper">
                                    <FaWhatsapp />
                                </div>

                                <div className="code-input-wrapper">
                                    <TextField
                                        fullWidth
                                        name="verification_code"
                                        placeholder="000000"
                                        value={formData.verification_code}
                                        onChange={handleInputChange}
                                        onBlur={() => handleBlur('verification_code')}
                                        error={!!errors.verification_code}
                                        helperText={errors.verification_code}
                                        disabled={isSubmitting}
                                        variant="outlined"
                                        inputProps={{
                                            maxLength: 6,
                                            style: { textAlign: 'center', fontSize: '32px', letterSpacing: '12px', fontWeight: 700 },
                                            autoComplete: "one-time-code"
                                        }}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    className="cBtnTheme"
                                    disabled={isSubmitting || formData.verification_code.length !== 6}
                                    sx={{ py: 1.5, fontSize: '16px', fontWeight: 600, mt: 2 }}
                                >
                                    {isSubmitting ? (
                                        <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> {t('signup.step3.verifying')}</>
                                    ) : (
                                        t('signup.step3.verify')
                                    )}
                                </Button>

                                <div className="resend-area">
                                    <span>{t('signup.step3.didntReceive')}</span>
                                    <button
                                        type="button"
                                        onClick={handleWhatsAppVerification}
                                        className="resend-link"
                                        disabled={resendCooldown > 0 || isSubmitting}
                                        style={{ opacity: resendCooldown > 0 ? 0.5 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' }}
                                    >
                                        {resendCooldown > 0 ? `${t('signup.step3.resendIn')} ${resendCooldown}s` : t('signup.step3.resend')}
                                    </button>
                                </div>

                                <button type="button" className="back-btn" onClick={() => setStep(2)}>
                                    ← {t('signup.step3.tryDifferent')}
                                </button>
                            </form>
                        )}
                    </>
                )}

                {/* Step 4: Set Password */}
                {step === 4 && (
                    <form onSubmit={handlePasswordSetup}>
                        <div className="success-icon-wrapper">
                            <FaCheck />
                        </div>

                        {/* Accessibility: Hidden username field for password manager association */}
                        <input
                            type="text"
                            name="username"
                            value={formData.email || formData.whatsapp}
                            autoComplete="username"
                            style={{ opacity: 0, position: 'absolute', width: 0, height: 0 }}
                            readOnly
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    className="inputOutline"
                                    fullWidth
                                    name="password"
                                    label={t('signup.step4.password')}
                                    type="password"
                                    placeholder={t('signup.step4.passwordPlaceholder')}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('password')}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    autoComplete="new-password"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: <FaLock className="field-icon" />,
                                    }}
                                />

                                {/* Password strength indicator */}
                                <div className="password-strength-indicator">
                                    <div className={`strength-rule ${passwordStrength.minLength ? 'valid' : ''}`}>
                                        {passwordStrength.minLength ? <FaCheck /> : <span className="rule-bullet">○</span>}
                                        <span>{t('signup.step4.requirements.minLength')}</span>
                                    </div>
                                    <div className={`strength-rule ${passwordStrength.hasUppercase ? 'valid' : ''}`}>
                                        {passwordStrength.hasUppercase ? <FaCheck /> : <span className="rule-bullet">○</span>}
                                        <span>{t('signup.step4.requirements.uppercase')}</span>
                                    </div>
                                    <div className={`strength-rule ${passwordStrength.hasLowercase ? 'valid' : ''}`}>
                                        {passwordStrength.hasLowercase ? <FaCheck /> : <span className="rule-bullet">○</span>}
                                        <span>{t('signup.step4.requirements.lowercase')}</span>
                                    </div>
                                    <div className={`strength-rule ${passwordStrength.hasNumber ? 'valid' : ''}`}>
                                        {passwordStrength.hasNumber ? <FaCheck /> : <span className="rule-bullet">○</span>}
                                        <span>{t('signup.step4.requirements.number')}</span>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    className="inputOutline"
                                    fullWidth
                                    name="confirm_password"
                                    label={t('signup.step4.confirmPassword')}
                                    type="password"
                                    placeholder={t('signup.step4.confirmPlaceholder')}
                                    value={formData.confirm_password}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('confirm_password')}
                                    error={!!errors.confirm_password}
                                    helperText={errors.confirm_password}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    autoComplete="new-password"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: <FaLock className="field-icon" />,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    className="cBtnTheme success-btn"
                                    disabled={isSubmitting}
                                    sx={{ py: 1.5, fontSize: '16px', fontWeight: 600, mt: 1 }}
                                >
                                    {isSubmitting ? (
                                        <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> {t('signup.step4.creating')}</>
                                    ) : (
                                        `🎉 ${t('signup.step4.complete')}`
                                    )}
                                </Button>
                            </Grid>
                        </Grid>

                        {/* NOTE: No back button on step 4 - verification is complete, going back would cause issues */}
                    </form>
                )}
            </Grid>
        </Grid >
    );
};

export default SignUpPage;